import {
  Body,
  H8,
  Input,
  Loader,
  Radio,
  Stack,
} from '@qred/components-library';
import debounce from 'lodash.debounce';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { AddressSearch } from '~/components/shared/AddressSearch/AddressSearch';
import { PostcodeAddress } from '~/hooks/usePostcode';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { useLazyGetCompanyInfoBySearchQuery } from '~/store/apis/endpoints/onboardingUnauthenticatedApi/companySearch/companySearch.api';
import {
  resetCompanyInForm,
  setIsCompanyTypeCategoryRequested,
  updateForm,
} from '~/store/slices/onboardingApplication.slice';

export const CompanySearchGermany = () => {
  const dispatch = useDispatch();
  const { market } = useSelector((state: RootState) => state.intl);
  const { companyName, connectId, companyAddress } = useSelector(
    (state: RootState) => state.onboardingApplication.form
  );
  const validationContext = useContext(ValidationContext);
  const t = useTranslate();
  const [nameField, setNameField] = useState<{
    touched: boolean;
    value: string;
  }>({
    touched: false,
    value: companyName ?? '',
  });
  const [addressField, setAddressField] = useState<{
    touched: boolean;
    value: string;
  }>({
    touched: false,
    value: companyAddress
      ? `${companyAddress.city}, ${companyAddress.street}`
      : '',
  });
  const [
    getCompanyInfoBySearch,
    { data: companySearchResults, isFetching, isSuccess, isError },
  ] = useLazyGetCompanyInfoBySearchQuery();

  const [selectedAddress, setSelectedAddress] = useState<string | null>(
    companyAddress ? `${companyAddress?.city}, ${companyAddress?.street}` : null
  );

  const handleSelectedAddress = (_selectedAddress: PostcodeAddress | null) => {
    if (_selectedAddress) {
      const {
        locality,
        postcode,
        street,
        buildingNumber,
      } = _selectedAddress.address;

      const fullAddress = `${street}, ${postcode}, ${locality} ${buildingNumber}`;
      setSelectedAddress(fullAddress);
    } else {
      setSelectedAddress(null);
    }
  };

  const handleSelectedCompany = (companyConnectId: string | undefined) => {
    const targetCompany = companySearchResults?.find(
      (company) => company.connectId === companyConnectId
    );

    if (targetCompany) {
      dispatch(
        updateForm({
          organizationNumber: targetCompany.companyNumber,
          companyName: targetCompany.companyName,
          vatNumber: targetCompany.vatNumber,
          connectId: targetCompany.connectId,
          companyAddress: targetCompany.address
            ? {
                street: targetCompany.address.street,
                zipCode: targetCompany.address.postcode,
                city: targetCompany.address.city,
              }
            : undefined,
          companyTypeCategory: targetCompany.companyTypeCategory,
        })
      );
      if (targetCompany.companyTypeCategory) {
        dispatch(setIsCompanyTypeCategoryRequested(true));
      }
    }
  };

  const getCompanyInfoBySearchDebounced = useCallback(
    debounce(
      (name: string, address: string) =>
        getCompanyInfoBySearch({ searchInput: name, market, address }, true),
      500
    ),
    []
  );

  useEffect(() => {
    if (nameField.value.trim() !== '' && selectedAddress) {
      getCompanyInfoBySearchDebounced(nameField.value, selectedAddress);
    } else {
      if (!connectId) {
        dispatch(resetCompanyInForm());
      }
    }
  }, [selectedAddress, nameField.value, connectId]);

  useEffect(() => {
    if (!connectId) {
      validationContext.addPropertyToValidationErrors('companySelectorStep');
    } else {
      validationContext.removePropertyFromValidationErrors(
        'companySelectorStep'
      );
    }

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'companySelectorStep'
      );
    };
  }, [connectId]);

  return (
    <Stack h="100%">
      <Input.Group orientation="vertical">
        <Input
          label={t('Onboarding.SelectCompanySearchByCompanyNameLabel')}
          value={nameField.value}
          onChange={(e) =>
            setNameField((prev) => ({ ...prev, value: e.target.value }))
          }
          onBlur={() => setNameField((prev) => ({ ...prev, touched: true }))}
          error={
            nameField.touched && !nameField.value
              ? t('ValidationErrors.Required')
              : undefined
          }
        />
        <AddressSearch
          label={t('Onboarding.SelectCompanySearchByCompanyAddressLabel')}
          handleResult={handleSelectedAddress}
          onBlur={() => setAddressField((prev) => ({ ...prev, touched: true }))}
          onChange={(value) =>
            setAddressField((prev) => ({ ...prev, value: value }))
          }
          error={
            addressField.touched && !addressField.value
              ? t('ValidationErrors.Required')
              : undefined
          }
        />
      </Input.Group>

      {isSuccess && !isFetching && (
        <Stack mt="md">
          <H8>
            {t('Onboarding.SelectCompanySearchByNameAndAddressSearchResults', {
              count: companySearchResults?.length,
            })}
          </H8>
          <Radio.Group
            direction="column"
            onChange={handleSelectedCompany}
            value={connectId}
          >
            {companySearchResults?.map((result) => (
              <Radio
                key={result.connectId}
                label={result.companyName}
                hint={
                  !!result.address &&
                  `${result.address.street}, ${result.address.postcode},
                      ${result.address.city}`
                }
                value={result.connectId}
              />
            ))}
          </Radio.Group>
        </Stack>
      )}

      {isError && (
        <Stack mt="md">
          <H8>
            {t('Onboarding.SelectCompanySearchByNameAndAddressSearchResults', {
              count: 0,
            })}
          </H8>
          <Body size={'lg'}>
            {t(
              'Onboarding.SelectCompanySearchByNameAndAddressNoSearchResultsFound'
            )}
          </Body>
        </Stack>
      )}

      {isFetching && (
        <Stack align="center" justify="center" mt={'md'}>
          <Loader />
        </Stack>
      )}
    </Stack>
  );
};
